import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import useFeatureFlags from '../../hooks/use-feature-flags';

import './affirm-as-low-as-display.less';

// How long to debounce affirm.ui.refresh()
const REFRESH_DEBOUNCE_WAIT = 100;

/**
 * Debouncing this function is a workaround for a problem where
 * `affirm.ui.refresh()` was called for every React component instance on the
 * page, which then generated a several API calls for every instance of the
 * Affirm messaging on the page. The result was that we ended up with an
 * exponential number of calls being made to Affirm APIs as we added more
 * Affirm messages.
 */
const debouncedRefresh = _.debounce(() => {
  const refresh = _.get(window, 'affirm.ui.refresh');
  if (!_.isFunction(refresh)) return;
  try {
    window.affirm.ui.refresh();
  } catch (e) {
    window.affirm.ui.ready(() => {
      window.affirm.ui.refresh();
    });
  }
}, REFRESH_DEBOUNCE_WAIT);

const AffirmAsLowAsDisplay = ({
  affirmColor,
  affirmType,
  amount,
  hideLearnMore,
  pageType,
  productName,
}) => {
  const featureFlags = useFeatureFlags();
  const hideAffirmForBasic = featureFlags['hide-affirm-for-basic'];
  const isBasic = /basic/.test(_.lowerCase(productName));
  const isIneligibleForFinancing = isBasic && hideAffirmForBasic;

  useEffect(() => {
    if (isIneligibleForFinancing) return;
    debouncedRefresh();
  }, [amount, isIneligibleForFinancing]);

  if (isIneligibleForFinancing) {
    return null;
  }

  return (
    <p
      className={`AffirmAsLowAsDisplay affirm-as-low-as ${pageType}`}
      data-amount={`${amount * 100}`}
      data-page-type={pageType}
      data-affirm-color={affirmColor}
      data-affirm-type={affirmType}
      data-learnmore-show={`${!hideLearnMore}`} />
  );
};

AffirmAsLowAsDisplay.propTypes = {
  affirmColor: PropTypes.oneOf(['black', 'white']),
  affirmType: PropTypes.oneOf(['text', 'symbol']),
  amount: PropTypes.number.isRequired,
  hideLearnMore: PropTypes.bool,
  pageType: PropTypes.string,
  productName: PropTypes.string,
};

AffirmAsLowAsDisplay.defaultProps = {
  affirmColor: undefined,
  affirmType: undefined,
  hideLearnMore: false,
  pageType: '',
  productName: '',
};

export default AffirmAsLowAsDisplay;
